import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/* Plain words for two directors who are not accountants. Every claim here is what the code
   does: books/poster.py (what is derived, reversal on change, the lock), domain/ledger.py
   (rates, openings against 3900, loans and capital through transfers), cli/mail_poll.py
   (the background posting). Change this text when they change. */

const LINK = "font-medium text-primary hover:underline";

function Term({ name, children }: { name: string; children: ReactNode }) {
  return (
    <div className="grid gap-x-4 sm:grid-cols-[11rem_1fr]">
      <dt className="font-medium">{name}</dt>
      <dd className="text-muted-foreground">{children}</dd>
    </div>
  );
}

export function BooksGuide() {
  return (
    <details className="group rounded-lg border bg-card text-sm">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 font-semibold [&::-webkit-details-marker]:hidden">
        <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" aria-hidden />
        How the books work: what each tab is for, and when you need to do anything
      </summary>
      <div className="flex flex-col gap-4 border-t px-3 py-3 leading-relaxed">
        <section className="flex flex-col gap-1">
          <h3 className="font-semibold">You do not type your records in here</h3>
          <p>
            Every invoice, credit note, payment, write-off, expense (bill or payout) and transfer
            you record in the app becomes its own entry in these books, in rupees, by itself. The
            books catch up when this page opens, when you press Post now, and every few minutes in
            the background. Recording a bill on <Link to="/expenses" className={LINK}>Expenses</Link>{" "}
            is all it takes; entering it here as well would count it twice. A receipt is the
            document you give a client for a payment already recorded, so there is nothing to bring
            in from receipts either.
          </p>
          <p>
            Change or void a record and its entry is reversed and, if the record still stands,
            posted again. Nothing is edited in place, so every correction stays visible. (An expense
            cannot be edited: void it and record it again, and the books follow.)
          </p>
        </section>

        <section className="flex flex-col gap-1">
          <h3 className="font-semibold">What each tab is for</h3>
          <dl className="flex flex-col gap-2">
            <Term name="Overview">
              This page: what is not in the books yet and why, and the latest entries made from your
              records. Normally nothing waits.
            </Term>
            <Term name="Statements">
              The trial balance, profit and loss and balance sheet for any period.
            </Term>
            <Term name="Ledger">
              One account's entries with a running balance, for example what a client owes or what
              went through a bank account.
            </Term>
            <Term name="Journals">
              Every entry, newest first, each linked back to its record. Day book (.xlsx) downloads
              the period for the CA; New journal is for the manual entries described below.
            </Term>
            <Term name="Chart of accounts">
              The accounts the entries go to. Add one only when the CA asks, for example
              Depreciation.
            </Term>
            <Term name="Exchange rates">
              The rupee rate of a currency on a day, for invoices, transfers and opening balances,
              and the fallback for a payment that has no rate of its own.
            </Term>
            <Term name="TDS and GST">
              The TDS deducted on bills by section and month with the deposit due dates, and the GST
              on invoices and on bills.
            </Term>
          </dl>
        </section>

        <section className="flex flex-col gap-1">
          <h3 className="font-semibold">When you need to do something</h3>
          <dl className="flex flex-col gap-2">
            <Term name="Normally: nothing">
              Record invoices, payments, expenses and transfers where you always do. They come in
              here by themselves.
            </Term>
            <Term name="When a rate is asked for">
              A foreign-currency invoice, transfer or opening balance waits until a rate is typed
              for its day or an earlier one; nothing is guessed. Press its Add the rate button in
              the list on this page (RBI reference rate or the bank's rate: ask the CA which). An
              entry keeps the rate it was posted with; if that rate was wrong, correct it and press
              Repost on the entry in Journals. Expenses carry their own rate, typed on the expense,
              so they never wait for one.
            </Term>
            <Term name="Payments in another currency">
              A payment is valued at its own figures: the INR credited by the bank when it lands
              in a rupee account, else its own rupee rate (the Rupee value when you record it, or
              Add rupee value on <Link to="/payments" className={LINK}>Payments</Link>). Only a
              payment with neither falls back to Exchange rates, and waits here when there is none
              for its day. Adding or changing a payment's rupee value later reverses its entry and
              posts it again, when that changes its rupees. The invoice is cleared at the rate it
              was booked at; the difference is the exchange gain or loss (4300).
            </Term>
            <Term name="Opening balances, once">
              Give each money account the balance it held and the day it was counted (
              <Link to="/money" className={LINK}>Money</Link>, Accounts); those post by themselves
              against Opening balance suspense (3900). The rest of the starting position (share
              capital, what clients owed, what the company owed) is one manual journal the CA types
              against the same account, so that it ends at zero.
            </Term>
            <Term name="Manual journals, rarely">
              Only for what the app does not record: bank interest, depreciation, the income-tax
              provision, accruals and the CA's other adjustments. A director's loan or capital
              that came in through the app is booked by itself: record it as a transfer from the
              director's own money account with the basis Loan or Capital. Never journal an
              invoice, payment, expense or transfer that is already in the app. A manual entry is
              not edited: reverse it and post the right one.
            </Term>
            <Term name="Lock a closed period">
              Once the CA has closed a quarter or the year, set Books locked through in{" "}
              <Link to="/settings" className={LINK}>Settings</Link>, Company. No manual entry can be
              dated on or before that day, and anything dated inside it or changed later is booked
              on the first open day, with its own date in the narration, so the closed figures never
              move.
            </Term>
            <Term name="For the CA">
              Statements, the Ledger, the day book from Journals (every line of the period and its
              trial balance) and TDS and GST. The profit and loss also shows an estimate of income
              tax at the rate in Settings with the advance-tax instalments; the actual provision is
              the CA's manual journal.
            </Term>
          </dl>
        </section>
      </div>
    </details>
  );
}
